import type { ModuleListener, UiModule } from '@krp/client-runtime'

import { createPhotosPorts } from './features/photos/api/photosPorts'
import { createPhotosListeners } from './features/photos/model/listeners'
import type { PhotosPortsFactory } from './features/photos/model/ports'
import { createProductsPorts } from './features/products/api/productsPorts'
import { createProductsListeners } from './features/products/model/listeners'
import type { ProductsPortsFactory } from './features/products/model/ports'
import { createInventoryPorts } from './features/inventory/api/inventoryPorts'
import { createInventoryListeners } from './features/inventory/model/listeners'
import type { InventoryPortsFactory } from './features/inventory/model/ports'
import { warehouseChoicePersistence } from './features/inventory/model/warehouseChoiceSlice'
import { createPriceTagsListeners } from './features/price-tags/model/listeners'
import { priceTagsPersistence } from './features/price-tags/model/persistence'
import { createSuppliesPorts } from './features/supplies/api/suppliesPorts'
import { createSuppliesListeners } from './features/supplies/model/listeners'
import type { SuppliesPortsFactory } from './features/supplies/model/ports'
import { onecUiReducer, type OneCUiState } from './state'
import { onecWindows } from './windows/registerWindows'

/** Порты фич пакета. По умолчанию — endpoints общего `baseApi`; тест подставляет свои. */
export interface OneCUiPorts {
  products: ProductsPortsFactory
  photos: PhotosPortsFactory
  /** Инвентаризация и скан ценников (UB1d). */
  inventory: InventoryPortsFactory
  supplies: SuppliesPortsFactory
}

/**
 * Модуль пакета для composition. Endpoints пакет регистрирует сам
 * (`baseApi.injectEndpoints` в `features/<фича>/api`, имена `onec*`, rid в
 * аргументах и тегах); приложение подключает модуль одной строкой.
 */
export function createOneCUiModule(ports: Partial<OneCUiPorts> = {}): UiModule<OneCUiState> {
  const listeners: ModuleListener<OneCUiState>[] = [
    ...createProductsListeners<OneCUiState>(ports.products ?? createProductsPorts, {
      intent: (state) => state.products,
      operations: (state) => state.productOperations,
    }),
    ...createPhotosListeners<OneCUiState>(ports.photos ?? createPhotosPorts, (state) => state.photos),
    ...createSuppliesListeners<OneCUiState>(ports.supplies ?? createSuppliesPorts, (state) => state.supplies),
    ...createInventoryListeners<OneCUiState>(ports.inventory ?? createInventoryPorts, {
      intent: (state) => state.inventory,
      choice: (state) => state.inventoryWarehouse,
    }),
    ...createPriceTagsListeners<OneCUiState>(ports.inventory ?? createInventoryPorts, {
      tags: (state) => state.priceTags,
      scan: (state) => state.priceTagScan,
    }),
  ]
  return {
    id: 'onec',
    reducer: onecUiReducer,
    slices: [
      {
        name: 'products',
        lifetime: 'screen',
        reason: 'поиск и черновики правки товара: до перезагрузки; о несохранённом окно предупреждает',
      },
      {
        name: 'productOperations',
        lifetime: 'screen',
        reason: 'ключ и итог команды 1С; пока исход неизвестен, окно предупреждает об уходе со страницы',
      },
      {
        name: 'photos',
        lifetime: 'screen',
        reason: 'выбранный кадр и загрузка: байты снимка живут только в памяти страницы',
      },
      {
        name: 'pricing',
        lifetime: 'screen',
        reason: 'набранная пара видов цен, список ключей и черновик закупки: потеря стоит одного ввода, серверных черновиков нет',
      },
      {
        name: 'importFlow',
        lifetime: 'screen',
        reason: 'разметка колонок, форма профиля и ключи попыток: серверный результат живёт в кэше, а файл накладной не сохраняется',
      },
      {
        name: 'supplies',
        lifetime: 'screen',
        reason:
          'фильтры, черновики правок поставки и ключи команд: сама поставка — серверный черновик (account), а несохранённое и команда без итога окно предупреждает при уходе',
      },
      {
        name: 'inventory',
        lifetime: 'screen',
        reason:
          'несохранённая дельта обхода и ключи команд: сама сессия — на сервере (account) и возобновляется после F5; о несохранённом окно предупреждает',
      },
      {
        name: 'inventoryWarehouse',
        lifetime: 'device',
        reason: 'склад инвентаризации, выбранный в каждой базе: после F5 обход того же склада открывается сам',
      },
      {
        name: 'priceTags',
        lifetime: 'device',
        reason: 'подбор ценников по базам — работа у полки, переживает F5; цены — снимок скана с его временем',
      },
      {
        name: 'structure',
        lifetime: 'screen',
        reason: 'поиск, свёрнутые узлы и выбранный элемент структуры 1С; сама структура — в кэше запроса и на сервере',
      },
      {
        name: 'receipts',
        lifetime: 'screen',
        reason: 'период, касса и раскрытый чек экрана «Чеки 1С»; сами чеки — в 1С и в кэше запроса',
      },
      {
        name: 'shifts',
        lifetime: 'screen',
        reason: 'место, месяц, день, кассир и состояние экрана «Смены 1С»; сами смены — в 1С и в кэше запроса',
      },
      {
        name: 'createDefaults',
        lifetime: 'screen',
        reason: 'изменённые, но не сохранённые поля «Создание по умолчанию» базы; сохранённое — на сервере и в кэше запроса',
      },
      {
        name: 'priceTagScan',
        lifetime: 'screen',
        reason: 'скан ценника в пути и итог прошлого скана',
      },
    ],
    windows: onecWindows,
    persistence: [
      warehouseChoicePersistence<OneCUiState>((state) => state.inventoryWarehouse),
      priceTagsPersistence<OneCUiState>((state) => state.priceTags),
    ],
    listeners,
  }
}
